import * as THREE from 'three';
import { WATER, TERRAIN } from '../config';
import { terrainHeight } from './terrain';
import { swellGLSL, chamferRelax } from './foam';
import { makeNormalTexture } from './textures';
import { cloudFieldGLSL, cloudUniforms } from './clouds';
import type { SkyUniforms } from './scene';

// Calm lake water, take 3. The previous version wrapped THREE.Water, which renders the WHOLE scene a
// second time into a mirror target every frame (the reflection visibly re-rendered as you moved, and
// the extra scene pass is what brought the frame stutter back). This one has NO render pass: it is a
// cheap unlit (MeshBasic) follow-plane whose "reflection" is the day-night SKY itself - it tints
// toward the live sky-dome colours (uZenith/uHorizon, shared from the sky uniforms) by fresnel, plus a
// sun glint aimed at the GLOBAL sun direction. So it reacts to the day-night cycle and the world sun
// (not the player's position), and it is rock-stable as you walk: nothing snaps or re-renders.
//
// On top of that we keep our look: a DEPTH tint (cyan shallows over the wet sand -> deep blue) from a
// per-vertex seabed attribute, shoreline foam, and a `toonAmount` of flat cartoon banding. The seabed
// is sampled at a coarse grid and bilinear-filled into the vertices; the plane follows the focus
// CONTINUOUSLY (no snap), and the seabed only re-fills when the focus actually moves, so the colour
// tracks the ground smoothly instead of stepping.

// Seabed depth is sampled on a WORLD-ANCHORED grid (origin snapped to GRID_STEP), NOT a camera-relative
// one. That is the key fix for "the water disappears as I move": with a follow-relative grid the sample
// points slid under the world as you walked, so a fixed channel was sampled by different points each
// frame and its depth swam (narrow channels smeared to ~0 depth and the water faded out). World-anchored,
// the depth at any world point is stable; the grid only re-bakes when the focus crosses a cell, and the
// overlap re-bakes to identical values, so there is no jump.
const GRID_STEP = 10; // metres per seabed cell (fine enough to resolve rivers; bilinear-smooth between)
const GW = Math.ceil(WATER.followSize / GRID_STEP) + 2; // grid cells across (+2 margin for the snap offset)
const GP = GW + 1; // grid points per row

// FAR OCEAN: the detailed follow-plane above only spans WATER.followSize, so water used to stop ~300m
// out while the terrain streams to ~900m, leaving a hard water cutoff mid-view. This second sheet is a
// big, cheap, FLAT plane at sea level that fills the horizon: it is depth-tested, so land occludes it,
// and it shows water wherever the terrain sits below the waterline, all the way to the fog line. No
// seabed sampling, one low-res quad, a light sky-reflective shader -> near-zero cost.
const FAR_SIZE = 14000; // huge (2x): the plane edge sits way past the fog + the haze makes it sky-coloured, so no visible edge
// Round-world CURVATURE: the far ocean's vertices ease DOWN by horizontal-distance^2 * OCEAN_CURVE. Kept GENTLE
// (a slight bow, ~20m drop by 1600m) -- a strong curve folded the plane into visible creases. The "endless sea"
// illusion comes mostly from the soft hazy horizon in the fragment shader, not the geometry curve.
const OCEAN_CURVE = 0.000008;

export class Water {
  readonly mesh: THREE.Mesh;
  readonly farMesh: THREE.Mesh; // big flat sea-level sheet that fills the horizon (added to the scene by main)
  private readonly mat: THREE.MeshBasicMaterial;
  private readonly seabed: THREE.BufferAttribute;
  private readonly openAttr: THREE.BufferAttribute; // distance-to-open-water: lakes/rivers max out -> calm swell
  private readonly hm = new Float64Array(GP * GP); // world-anchored terrainHeight grid
  private readonly sd = new Float32Array(GP * GP); // scratch: chamfer distance-to-land
  private readonly od = new Float32Array(GP * GP); // chamfer distance-to-open-water
  private readonly localX: Float32Array; // per-vertex plane-local x/z (worldX = focus + localX)
  private readonly localZ: Float32Array;
  private gx0 = NaN; // current snapped grid origin (world)
  private gz0 = NaN;
  private lastX = NaN;
  private lastZ = NaN;
  private readonly uTime: { value: number };
  // Smooth, mipmapped tangent-space normal map for the ripples (sampled twice in the shader, scrolled).
  // Mipmaps are what kill the per-pixel speckle: distant texels average to a flat normal instead of
  // aliasing, so the highlight built on it stays smooth. Built ONCE (CPU canvas), reused every frame.
  private readonly rippleTex = makeNormalTexture({ size: 256, octaves: 4, period: 8, strength: 1.6 });
  // Our own uniforms (depth tint / foam / ripples / reflection look). The sky/sun colours are SHARED
  // from the sky uniforms (assigned in onBeforeCompile) so the day-night cycle drives them for free.
  private readonly cu = {
    uSeaLevel: { value: TERRAIN.seaLevel },
    uDepthFull: { value: WATER.depthFull },
    uShoreFoam: { value: WATER.shoreFoam },
    uShallowAlpha: { value: WATER.shallowAlpha },
    uOpacityMax: { value: WATER.opacityMax },
    uToon: { value: WATER.toonAmount },
    uRippleStr: { value: WATER.distortionScale * 0.05 },
    uRippleFreq: { value: WATER.rippleScale * 0.035 },
    uRippleSpeed: { value: WATER.rippleSpeed },
    uReflect: { value: WATER.reflectStrength },
    uGlint: { value: WATER.glint },
    uSoftEdge: { value: WATER.softEdge },
    uSwellAmp: { value: WATER.swellAmp },
    uShallow: { value: new THREE.Color(WATER.shallowColor).convertSRGBToLinear() },
    uDeep: { value: new THREE.Color(WATER.deepColor).convertSRGBToLinear() },
    uStormW: cloudUniforms.uStorm, // shared storm intensity: overcast greys the pond/lake body (owner 2026-07-12)
    uFoam: { value: new THREE.Color(WATER.foamColor).convertSRGBToLinear() },
  };
  // A wielded torch lights the water surface (x, z, intensity 0..1, range), like the grass.
  private readonly cuTorch = { value: new THREE.Vector4(0, 0, 0, 1) };

  constructor(uTime: { value: number }, sky: SkyUniforms) {
    this.uTime = uTime;

    // Flat plane in XZ (rotation baked into the geometry, so the mesh has no rotation: the shader
    // normal is +Y and world xz comes straight from the model matrix).
    const geom = new THREE.PlaneGeometry(WATER.followSize, WATER.followSize, WATER.segments, WATER.segments);
    geom.rotateX(-Math.PI / 2);
    const n = geom.attributes.position.count;
    this.seabed = new THREE.BufferAttribute(new Float32Array(n).fill(TERRAIN.seaLevel - 5), 1);
    this.seabed.setUsage(THREE.DynamicDrawUsage);
    geom.setAttribute('seabed', this.seabed);
    this.openAttr = new THREE.BufferAttribute(new Float32Array(n).fill(0), 1);
    this.openAttr.setUsage(THREE.DynamicDrawUsage);
    geom.setAttribute('openDist', this.openAttr);

    // Store each vertex's plane-local x/z. The plane is centered on the focus, so a vertex's WORLD xz is
    // (focus + local); refreshSeabed bilinear-samples the world-anchored grid at that world position.
    const pos = geom.attributes.position;
    this.localX = new Float32Array(n);
    this.localZ = new Float32Array(n);
    for (let k = 0; k < n; k++) { this.localX[k] = pos.getX(k); this.localZ[k] = pos.getZ(k); }

    // Unlit base: we compute the entire colour ourselves (depth + sky reflection + glint + foam), so we
    // want NO lighting added on top. MeshBasic still gives us fog + tone-map + colour-space for free.
    this.mat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: true });
    this.mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.cu, {
        uTime: this.uTime,
        uZenith: sky.uZenith,
        uHorizon: sky.uHorizon,
        uSunColor: sky.uSunColor,
        uSunDir: sky.uSunDir,
        uCloudCover: cloudUniforms.uCloudCover,
        uRipple: { value: this.rippleTex },
        uTorch: this.cuTorch,
      });

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
          attribute float seabed; attribute float openDist; uniform float uSeaLevel; uniform float uTime; uniform float uSwellAmp;
          varying float vDepth; varying vec3 vWPos; varying vec3 vView; varying vec2 vSwellG; varying vec2 vWLocal;
          ${swellGLSL}`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          vDepth = uSeaLevel - seabed;
          vec4 oxWP = modelMatrix * vec4(transformed, 1.0);
          // 3D SWELL: displace by the shared wave-height field (same fn as the foam sheet, which rides
          // on top). Faded out by plane-local radius WELL before the horizon: past ~200m the 19-26m
          // trains alias into moiré corduroy on 5m quads (and expose the far-sheet seam), and fog owns
          // that range anyway. Already ~0 below 1.2m depth so the shore soft-edge stays undisturbed.
          float oxSwFade = 1.0 - smoothstep(150.0, 280.0, length(position.xz));
          // CALM RULE: enclosed lakes/rivers (openDist maxed) damp the swell to a gentle lap; the
          // foam sheet applies the IDENTICAL factor so it keeps riding the surface exactly.
          oxSwFade *= mix(1.0, 0.15, smoothstep(115.0, 180.0, openDist));
          vec2 oxSwG;
          float oxSw = oxSwell(oxWP.xz, vDepth, uTime, uSwellAmp * oxSwFade, oxSwG);
          transformed.y += oxSw;
          oxWP.y += oxSw;
          vSwellG = oxSwG;
          vWLocal = position.xz;
          vWPos = oxWP.xyz;
          vView = cameraPosition - oxWP.xyz;`,
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
          ${cloudFieldGLSL}
          varying float vDepth; varying vec3 vWPos; varying vec3 vView; varying vec2 vSwellG; varying vec2 vWLocal;
          uniform vec3 uShallow, uDeep, uFoam, uZenith, uHorizon, uSunColor, uSunDir;
          uniform float uStormW;
          uniform vec4 uTorch;
          uniform float uTime, uDepthFull, uShoreFoam, uShallowAlpha, uOpacityMax, uToon, uCloudCover;
          uniform float uRippleStr, uRippleFreq, uRippleSpeed, uReflect, uGlint, uSoftEdge;
          uniform sampler2D uRipple;
          float oxHash(vec2 p){ p = mod(p, 1024.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
          float oxNoise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
            float a = oxHash(i), b = oxHash(i + vec2(1.0, 0.0)), c = oxHash(i + vec2(0.0, 1.0)), d = oxHash(i + vec2(1.0, 1.0));
            return mix(mix(a, b, f.x), mix(c, d, f.x), f.y); }
          // Smooth animated water normal: two scrolled samples of the tiled normal map at different
          // scales/directions, averaged. Mipmapped -> averages with distance instead of aliasing (no
          // per-pixel speckle). World up is +Y and tangents are +X/+Z (the plane bakes rotateX(-90)),
          // so the tangent-space slope maps straight to world with no TBN matrix.
          vec3 oxWaterNormal(vec2 wxz){
            vec2 uv1 = wxz * uRippleFreq        + vec2( uTime * uRippleSpeed,       uTime * uRippleSpeed * 0.6);
            vec2 uv2 = wxz * uRippleFreq * 2.17 + vec2(-uTime * uRippleSpeed * 0.8, uTime * uRippleSpeed * 0.45);
            vec3 t1 = texture2D(uRipple, uv1).xyz * 2.0 - 1.0;
            vec3 t2 = texture2D(uRipple, uv2).xyz * 2.0 - 1.0;
            vec2 t  = (t1.xy + t2.xy) * 0.5;
            return normalize(vec3(t.x * uRippleStr, 1.0, t.y * uRippleStr));
          }`,
        )
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>
          {
            float depthT = smoothstep(0.0, max(uDepthFull, 0.1), max(vDepth, 0.0));
            // SOFT EDGE: dissolve alpha to 0 over the last uSoftEdge metres of depth, JITTERED per-fragment
            // by slow-morphing world noise. The plain per-vertex fade died inside one 8m water quad, which
            // drew the waterline as a hard polygon cutoff; the noise breaks that into a ragged organic melt
            // (jitter scales with the band so tight admin presets still behave).
            float edgeJit = (oxNoise(vWPos.xz * 1.6 + uTime * 0.08) - 0.5) * uSoftEdge * 0.7;
            float edge = smoothstep(0.0, uSoftEdge, max(vDepth, 0.0) + edgeJit);
            // SMOOTH ripple normal from the mipmapped tiled texture (no per-pixel noise derivative -> no
            // speckle). World-anchored UVs (vWPos.xz) so the ripples do not swim as the plane recenters.
            vec3 N = oxWaterNormal(vWPos.xz);
            // Tilt the shading normal down the swell faces (interpolated slope from the vertex stage),
            // so the sun sheen and sky tint ROLL along the moving waves -- the "3D" read at a glance.
            N = normalize(N + vec3(-vSwellG.x, 0.0, -vSwellG.y) * 2.2);
            vec3 V = normalize(vView);
            // SOFT broad sun sheen (moderate Blinn power, low strength); gated DOWN in the shallows so it
            // never blooms over the bright sand, faded at night. Tracks the GLOBAL sun, NOT the camera, so
            // it does not cause the move-shift the user disliked.
            vec3 H = normalize(normalize(uSunDir) + V);
            float dayFac = smoothstep(-0.08, 0.18, uSunDir.y);
            float sheen = pow(max(dot(N, H), 0.0), 28.0) * uGlint * 0.35 * dayFac * mix(0.06, 1.0, depthT);
            // Depth body colour (blue-green shallows -> deep blue), optional toon banding. NO fresnel sky
            // reflection: the water holds this stable colour instead of shifting as the camera moves.
            vec3 body = mix(uShallow, uDeep, depthT);
            float bandT = floor(depthT * 4.0 + 0.5) / 4.0;
            body = mix(body, mix(uShallow, uDeep, bandT), uToon);
            // Sky reflection is OFF by default (uReflect = 0 -> col = body, a stable colour that does NOT
            // shift as the camera moves). The F2 "Sky reflection" knob can dial it back in if wanted.
            float fres = clamp(pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 5.0), 0.0, 1.0);
            vec3 skyCol = mix(uHorizon, uZenith, clamp(reflect(-V, N).y, 0.0, 1.0));
            vec3 col = mix(body, skyCol, clamp(fres * uReflect * mix(0.12, 1.0, depthT), 0.0, 0.85));
            col += uSunColor * sheen;
            // SUN GLITTER LANE: discrete sparkles that only survive inside the tight sun-reflection
            // band (pow 90 alignment), multiplying as the sun drops -- the classic shore-to-horizon
            // glitter path at sunset. Rides the existing ripple normal + sun dir; ~4 lines.
            float glitA = pow(max(dot(N, H), 0.0), 90.0);
            float glitN = oxNoise(vWPos.xz * 6.1 + vec2(uTime * 1.7, -uTime * 1.3));
            float lowSunW = 0.35 + 1.4 * (1.0 - smoothstep(0.12, 0.45, uSunDir.y));
            col += uSunColor * smoothstep(0.74, 0.9, glitN) * glitA * uGlint * lowSunW * dayFac * mix(0.1, 1.0, depthT);
            // Shoreline foam: a SMOOTH band that begins just past the soft-edge fade and falls off lakeward,
            // so the now-invisible contact zone (vDepth < uSoftEdge) never erases it.
            float foamStart = uSoftEdge;
            float foamEnd = uSoftEdge + max(uShoreFoam, 0.02);
            float foam = 1.0 - smoothstep(foamStart, foamEnd, max(vDepth, 0.0));
            foam = smoothstep(0.0, 1.0, foam);
            foam *= 0.6 + 0.4 * (N.x + N.z); // faint ripple life, still smooth (no noisy dots)
            col = mix(col, uFoam, clamp(foam, 0.0, 1.0) * 0.6);
            // Global day-night light reaches the unlit water too: at night it bottoms out near-dark (matching
            // the world's near-zero ambient) instead of glowing blue, then a wielded torch lights it warmly.
            col *= mix(vec3(0.025, 0.032, 0.06), vec3(1.0), dayFac);
            // STORM: overcast flattens the body toward the pale grey-blue of the sky it mirrors -- without
            // this a small pond stayed a saturated navy disc punched into grey rain-lit terrain (owner:
            // "wtf is this"). Scaled by dayFac so night water stays night-dark.
            col = mix(col, vec3(0.32, 0.36, 0.42) * dayFac, 0.45 * clamp(uStormW, 0.0, 1.0));
            float wtd = distance(vWPos.xz, uTorch.xy);
            float wtl = uTorch.z * clamp(1.0 - wtd / max(uTorch.w, 0.001), 0.0, 1.0);
            col += vec3(1.1, 0.62, 0.32) * (wtl * wtl) * 0.6; // torch glints warm on the water near you
            // Clear over the sand in the shallows, opaque in the deep; foam adds opacity; the soft edge
            // (alpha -> 0 at the waterline) is the FINAL multiply so the contact line is never drawn.
            float a = mix(uShallowAlpha, uOpacityMax, depthT);
            a = max(a, foam * 0.65);
            a *= edge;
            // RIM CROSSFADE: dissolve the detail sheet over its last ~140m so the FAR ocean sheet
            // (rendered beneath, renderOrder 0) shows through -- the square seam at the 600m sheet
            // boundary melts away instead of drawing a visible step. Costs one smoothstep.
            a *= 1.0 - smoothstep(430.0, 570.0, length(vWLocal));
            col *= mix(1.0, oxCloudShadow(vWPos.xz, uSunDir, uTime, uCloudCover), 0.75); // drifting cloud shadow dims the water (a touch gentler than land)
            diffuseColor = vec4(col, clamp(a, 0.0, 1.0));
          }`,
        );
    };
    this.mat.customProgramCacheKey = () => 'ox-calm-water-v9-glitter';

    this.mesh = new THREE.Mesh(geom, this.mat);
    this.mesh.position.y = TERRAIN.seaLevel;
    this.mesh.name = 'water';
    this.mesh.renderOrder = 1;
    this.mesh.frustumCulled = false; // the follow-plane is always under the camera

    this.farMesh = this.buildFarPlane(sky);
  }

  // The far horizon ocean: a low-res flat plane at sea level, sky-reflective, fog-faded. Rendered BEFORE
  // the detailed follow-plane (renderOrder 0) and depth-tested, so land occludes it and the near plane's
  // shoreline detail sits on top. It has no seabed/foam: everything it covers is "deep + distant".
  private buildFarPlane(sky: SkyUniforms): THREE.Mesh {
    const geom = new THREE.PlaneGeometry(FAR_SIZE, FAR_SIZE, 96, 96); // more segments so the curvature reads smooth
    geom.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false }); // fog OFF: the haze blend to the SKY's horizon colour is the only far-fade, so the edge matches the sky exactly (no seam)
    mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, {
        uTime: this.uTime,
        uZenith: sky.uZenith,
        uHorizon: sky.uHorizon,
        uSunColor: sky.uSunColor,
        uSunDir: sky.uSunDir,
        uRipple: { value: this.rippleTex },
        uDeep: this.cu.uDeep,
        uRippleStr: this.cu.uRippleStr,
        uRippleFreq: this.cu.uRippleFreq,
        uRippleSpeed: this.cu.uRippleSpeed,
        uGlint: this.cu.uGlint,
      });
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n varying vec3 vWPos; varying vec3 vView; varying vec3 vFlatView;`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          vec3 oxFlatWP = (modelMatrix * vec4(transformed, 1.0)).xyz;            // FLAT (pre-curve) world pos -> shade from this
          transformed.y -= dot(transformed.xz, transformed.xz) * ${OCEAN_CURVE}; // gentle curve: silhouette only (no shading facets)
          vec4 oxWP = modelMatrix * vec4(transformed, 1.0);
          vWPos = oxWP.xyz; vView = cameraPosition - oxWP.xyz; vFlatView = cameraPosition - oxFlatWP;`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
          varying vec3 vWPos; varying vec3 vView; varying vec3 vFlatView;
          uniform vec3 uDeep, uZenith, uHorizon, uSunColor, uSunDir;
          uniform float uTime, uRippleStr, uRippleFreq, uRippleSpeed, uGlint;
          uniform sampler2D uRipple;
          vec3 oxFarNormal(vec2 wxz){
            vec2 uv1 = wxz * uRippleFreq        + vec2( uTime * uRippleSpeed,       uTime * uRippleSpeed * 0.6);
            vec2 uv2 = wxz * uRippleFreq * 2.17 + vec2(-uTime * uRippleSpeed * 0.8, uTime * uRippleSpeed * 0.45);
            vec3 t1 = texture2D(uRipple, uv1).xyz * 2.0 - 1.0;
            vec3 t2 = texture2D(uRipple, uv2).xyz * 2.0 - 1.0;
            vec2 t  = (t1.xy + t2.xy) * 0.5;
            return normalize(vec3(t.x * uRippleStr, 1.0, t.y * uRippleStr));
          }`)
        .replace('#include <color_fragment>', `#include <color_fragment>
          {
            float dist = length(vFlatView); // FLAT distance (ignores the curve's per-vertex Y) -> smooth bands, no facet creases
            // Ripple + glint DETAIL fades out with distance: the far sea flattens to glass-smooth so the horizon
            // reads soft/blurred (a depth-of-field-like smear) instead of a crisp rippled line.
            float detail = 1.0 - smoothstep(280.0, 1500.0, dist);
            vec3 N = normalize(mix(vec3(0.0, 1.0, 0.0), oxFarNormal(vWPos.xz), detail));
            vec3 V = normalize(vFlatView);
            float fres = clamp(pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 4.0), 0.0, 1.0);
            vec3 skyCol = mix(uHorizon, uZenith, clamp(reflect(-V, N).y, 0.0, 1.0));
            vec3 col = mix(uDeep, skyCol, clamp(0.22 + fres * 0.7, 0.0, 0.90));
            vec3 H = normalize(normalize(uSunDir) + V);
            float dayFac = smoothstep(-0.08, 0.18, uSunDir.y);
            col += uSunColor * pow(max(dot(N, H), 0.0), 60.0) * uGlint * dayFac * detail;
            col *= mix(vec3(0.025, 0.032, 0.06), vec3(1.0), dayFac); // near-dark at night like the rest of the world
            // ATMOSPHERIC SCATTER: the deep blue reads out to ~mid distance, then the sea dissolves FULLY into the
            // sky's horizon colour over a long soft band -> a hazy, scattered, no-hard-line horizon AND a plane edge
            // that is pure sky colour (invisible). 100% blend at the far so the 14km edge matches the sky exactly.
            float haze = smoothstep(1200.0, 4500.0, dist);
            col = mix(col, uHorizon, haze);
            // Fade IN near so the detailed follow-plane + sandy shallows read close; opaque toward the horizon.
            float a = smoothstep(280.0, 460.0, dist) * 0.96;
            diffuseColor = vec4(col, a);
          }`);
    };
    mat.customProgramCacheKey = () => 'ox-far-water-v5';
    const m = new THREE.Mesh(geom, mat);
    m.position.y = TERRAIN.seaLevel;
    m.name = 'water-far';
    m.renderOrder = 0; // before the detailed follow-plane (1), so its shoreline detail draws on top
    m.frustumCulled = false;
    return m;
  }

  // A wielded torch lights nearby water (world x/z, intensity 0 = off, range). Same idea as the grass.
  setTorch(x: number, z: number, intensity: number, range: number): void {
    this.cuTorch.value.set(x, z, intensity, range);
  }

  // Recenter (continuous, no snap) + push the live look into the shader each frame. The sky/sun colours
  // update themselves (shared uniforms driven by the day-night cycle), so we never touch them here.
  update(focusX: number, focusZ: number): void {
    this.mesh.position.x = focusX;
    this.mesh.position.z = focusZ;
    this.farMesh.position.set(focusX, TERRAIN.seaLevel, focusZ); // the horizon sheet rides the player too
    this.farMesh.visible = true; // (restored if the water debug toggle hid it)
    // Re-fill the seabed only when the plane actually moved (cheap, and it removes any stepping).
    const dx = focusX - this.lastX, dz = focusZ - this.lastZ;
    if (!(dx * dx + dz * dz < 0.01)) {
      this.lastX = focusX;
      this.lastZ = focusZ;
      this.refreshSeabed(focusX, focusZ);
    }
    const cu = this.cu;
    cu.uSeaLevel.value = TERRAIN.seaLevel;
    this.mesh.position.y = TERRAIN.seaLevel;
    cu.uDepthFull.value = WATER.depthFull;
    cu.uShoreFoam.value = WATER.shoreFoam;
    cu.uShallowAlpha.value = WATER.shallowAlpha;
    cu.uOpacityMax.value = WATER.opacityMax;
    cu.uToon.value = WATER.toonAmount;
    cu.uRippleStr.value = WATER.distortionScale * 0.05;
    cu.uRippleFreq.value = WATER.rippleScale * 0.035;
    cu.uRippleSpeed.value = WATER.rippleSpeed;
    cu.uReflect.value = WATER.reflectStrength;
    cu.uGlint.value = WATER.glint;
    cu.uSoftEdge.value = WATER.softEdge;
    cu.uSwellAmp.value = WATER.swellAmp;
    cu.uShallow.value.set(WATER.shallowColor).convertSRGBToLinear();
    cu.uDeep.value.set(WATER.deepColor).convertSRGBToLinear();
    cu.uFoam.value.set(WATER.foamColor).convertSRGBToLinear();
  }

  private refreshSeabed(fx: number, fz: number): void {
    const half = WATER.followSize / 2;
    const hm = this.hm;
    // World-anchored grid origin: snap to GRID_STEP so the sample points sit at FIXED world locations.
    const gx0 = Math.floor((fx - half) / GRID_STEP) * GRID_STEP;
    const gz0 = Math.floor((fz - half) / GRID_STEP) * GRID_STEP;
    // Re-bake the terrainHeight grid only when the focus crosses a cell (the overlap re-bakes identical,
    // so there is no jump). Between crossings the grid is reused; only the cheap per-vertex resample runs.
    if (gx0 !== this.gx0 || gz0 !== this.gz0) {
      this.gx0 = gx0;
      this.gz0 = gz0;
      let minH = Infinity;
      for (let j = 0; j < GP; j++) {
        for (let i = 0; i < GP; i++) {
          const h = terrainHeight(gx0 + i * GRID_STEP, gz0 + j * GRID_STEP);
          hm[j * GP + i] = h;
          if (h < minH) minH = h;
        }
      }
      // No seabed within the plane below sea -> no water in view: hide it (skips the draw). Small margin
      // so a sliver of water at the plane edge does not flicker the whole sheet on/off.
      this.mesh.visible = minH < TERRAIN.seaLevel + 0.5;
      // OPEN-WATER DETECTOR (ocean vs lake vs river), matching the foam sheet: distance to the nearest
      // cell that is itself >=95m from any land. Lakes/rivers max out -> the vertex shader damps the
      // swell to a gentle lap there. Two chamfer passes over ~15k cells, only when the grid re-bakes.
      const sea = TERRAIN.seaLevel, sd = this.sd, od = this.od;
      for (let k = 0; k < sd.length; k++) sd[k] = hm[k] > sea + 0.05 ? 0 : 1e9;
      chamferRelax(sd, GP, GRID_STEP);
      for (let k = 0; k < od.length; k++) od[k] = sd[k] >= 95 ? 0 : 1e9;
      chamferRelax(od, GP, GRID_STEP);
    }
    // Per-vertex bilinear at the vertex's CURRENT world position (runs every move so the contact line
    // tracks the terrain). World-anchored grid -> the depth at a fixed world point is identical regardless
    // of where the plane sits, so nothing swims as you walk.
    const arr = this.seabed.array as Float32Array;
    const opn = this.openAttr.array as Float32Array;
    const od = this.od;
    const lx = this.localX, lz = this.localZ, inv = 1 / GRID_STEP;
    for (let k = 0; k < arr.length; k++) {
      const u = (fx + lx[k] - gx0) * inv;
      const v = (fz + lz[k] - gz0) * inv;
      let i = u | 0, j = v | 0;
      if (i < 0) i = 0; else if (i >= GW) i = GW - 1;
      if (j < 0) j = 0; else if (j >= GW) j = GW - 1;
      const tx = u - i, tz = v - j, b = j * GP + i;
      const w00 = (1 - tx) * (1 - tz), w10 = tx * (1 - tz), w01 = (1 - tx) * tz, w11 = tx * tz;
      arr[k] = hm[b] * w00 + hm[b + 1] * w10 + hm[b + GP] * w01 + hm[b + GP + 1] * w11;
      const o = od[b] * w00 + od[b + 1] * w10 + od[b + GP] * w01 + od[b + GP + 1] * w11;
      opn[k] = o > 240 ? 240 : o; // fully-enclosed water never sees an open cell -> clamp = full calm
    }
    this.seabed.needsUpdate = true;
    this.openAttr.needsUpdate = true;
  }
}
